import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { ChevronLeftIcon, EyeIcon, EyeSlashIcon, PhoneIcon, LockClosedIcon, UserIcon } from '@heroicons/react/24/outline';

export default function Register() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('583851502201');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();

  useEffect(() => {
    // Check if user is already logged in
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      navigate('/home');
    }
  }, [navigate]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!phoneNumber || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (!agreedToPrivacy) {
      toast.error('Please agree to the Privacy Agreement');
      setIsLoading(false);
      return;
    }

    try {
      const result = await register({ phoneNumber, password });
      if (result.success) {
        toast.success('Registration successful!');
        // Store user data in localStorage
        localStorage.setItem('user', JSON.stringify(result.user));
        // Redirect to home page
        navigate('/home');
      } else {
        toast.error(result.error || 'Registration failed');
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred during registration');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-white">
      {/* Top Section with Coral Background */}
      <div className="bg-[#F47B6C] p-6 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeftIcon className="h-6 w-6 text-white" />
          </button>
          <h1 className="text-xl font-bold text-white">Bounty Game</h1>
          <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1.5">
            <img src="https://flagcdn.com/w20/us.png" alt="EN" className="w-5 h-5 rounded-full" />
            <span className="text-lg font-medium text-white">EN</span>
          </div>
        </div>

        {/* Title */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold mb-2 text-white">Register</h1>
          <p className="text-white/80 text-sm">Please register by phone number or email</p>
        </div>
      </div>

      {/* White Background Content Section */}
      <div className="bg-white -mt-6 rounded-t-[2rem] p-6 min-h-[calc(100vh-16rem)] shadow-sm">
        {/* Register Form */}
        <form onSubmit={handleRegister} className="space-y-6">
          {/* Phone Icon and Title */}
          <div className="flex flex-col items-center mb-6">
            <div className="bg-[#F47B6C]/10 rounded-lg p-3 mb-3">
              <PhoneIcon className="h-6 w-6 text-[#F47B6C]" />
            </div>
            <h2 className="text-[#F47B6C] text-xl">Register your phone</h2>
          </div>

          {/* Phone Number Input */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <PhoneIcon className="h-5 w-5 text-[#F47B6C]" />
              <span className="text-base text-gray-700">Phone number</span>
            </label>
            <div className="flex gap-2">
              <select className="bg-gray-50 rounded-xl px-4 py-3 w-24 text-gray-700 border border-gray-200">
                <option>+91</option>
              </select>
              <input
                type="tel"
                placeholder="Please enter the phone number"
                className="bg-gray-50 rounded-xl px-4 py-3 w-full text-gray-700 placeholder-gray-400 border border-gray-200"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Password Input */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <LockClosedIcon className="h-5 w-5 text-[#F47B6C]" />
              <span className="text-base text-gray-700">Set password</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Set password"
                className="bg-gray-50 rounded-xl px-4 py-3 w-full text-gray-700 placeholder-gray-400 border border-gray-200"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showPassword ? (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password Input */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <LockClosedIcon className="h-5 w-5 text-[#F47B6C]" />
              <span className="text-base text-gray-700">Confirm password</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm password"
                className="bg-gray-50 rounded-xl px-4 py-3 w-full text-gray-700 placeholder-gray-400 border border-gray-200"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showConfirmPassword ? (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Invite Code Input */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <UserIcon className="h-5 w-5 text-[#F47B6C]" />
              <span className="text-base text-gray-700">Invite code</span>
            </label>
            <input
              type="text"
              value={inviteCode}
              className="bg-gray-50 rounded-xl px-4 py-3 w-full text-gray-700 border border-gray-200"
              readOnly
            />
          </div>

          {/* Privacy Agreement */}
          <div className="flex items-center gap-2 mt-4">
            <input
              type="checkbox"
              id="privacy"
              className="h-5 w-5 rounded border-gray-300 text-[#F47B6C] focus:ring-[#F47B6C]"
              checked={agreedToPrivacy}
              onChange={(e) => setAgreedToPrivacy(e.target.checked)}
              disabled={isLoading}
            />
            <label htmlFor="privacy" className="text-gray-600 text-sm">
              I have read and agree{' '}
              <Link to="/privacy" className="text-[#F47B6C] underline">
                [Privacy Agreement]
              </Link>
            </label>
          </div>

          {/* Register Button */}
          <button
            type="submit"
            className="w-full bg-[#F47B6C] text-white rounded-full py-3.5 text-base font-medium mt-6"
            disabled={isLoading}
          >
            {isLoading ? 'Registering...' : 'Register'}
          </button>

          {/* Login Link */}
          <div className="text-center mt-4">
            <Link 
              to="/login" 
              className="inline-flex items-center justify-center w-full bg-white text-gray-600 rounded-full py-3.5 text-base font-medium border border-[#F47B6C]"
            >
              <span className="text-gray-600">I have an account</span>
              <span className="text-[#F47B6C] ml-1">Login</span>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
} 